import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  CardMedia,
  Chip,
  IconButton,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFoodcourts, deleteFoodcourt } from 'apis/foodcourtAPIs';
import { useDispatch } from 'react-redux';
import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  searchBar: {
    marginBottom: theme.spacing(3),
    maxWidth: 400,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[6],
    },
  },
  media: {
    height: 180,
    backgroundColor: theme.palette.grey[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderIcon: {
    fontSize: 64,
    color: theme.palette.grey[400],
  },
  cardContent: {
    flexGrow: 1,
  },
  cardActions: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
  },
  uiProgress: {
    position: 'fixed',
    zIndex: 1000,
    left: '50%',
    top: '35%',
  },
}));

const FoodcourtList = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: foodcourts = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['foodcourts'],
    queryFn: getFoodcourts,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFoodcourt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foodcourts'] });
      dispatch(showSuccessSnackbar('Foodcourt deleted successfully'));
    },
    onError: (err) => {
      dispatch(showErrorSnackbar(err.response?.data?.message || 'Failed to delete foodcourt'));
    },
  });

  const handleDelete = (e, foodcourtId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this foodcourt?')) {
      deleteMutation.mutate(foodcourtId);
    }
  };

  const filteredFoodcourts = foodcourts.filter((fc) =>
    fc.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className={classes.root}>
        <CircularProgress size={80} className={classes.uiProgress} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={classes.root}>
        <Alert severity="error">
          {error?.response?.data?.message || 'Error loading foodcourts'}
          <br />
          <Button onClick={() => refetch()} sx={{ mt: 1 }}>Retry</Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">Foodcourts</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => history.push('/foodcourt/new')}
        >
          Create Foodcourt
        </Button>
      </div>

      <TextField
        className={classes.searchBar}
        fullWidth
        size="small"
        placeholder="Search foodcourts..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {filteredFoodcourts.length === 0 ? (
        <Box textAlign="center" py={8}>
          <StorefrontIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            {searchTerm ? 'No foodcourts match your search' : 'No foodcourts yet'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => history.push('/foodcourt/new')}
              sx={{ mt: 2 }}
            >
              Create your first foodcourt
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredFoodcourts.map((foodcourt) => (
            <Grid item xs={12} sm={6} md={4} key={foodcourt.id}>
              <Card
                className={classes.card}
                onClick={() => history.push(`/foodcourt/${foodcourt.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                {foodcourt.imageUrl ? (
                  <CardMedia
                    component="img"
                    className={classes.media}
                    image={foodcourt.imageUrl}
                    alt={foodcourt.name}
                  />
                ) : (
                  <div className={classes.media}>
                    <StorefrontIcon className={classes.placeholderIcon} />
                  </div>
                )}
                <CardContent className={classes.cardContent}>
                  <Typography variant="h6" gutterBottom>
                    {foodcourt.name}
                  </Typography>
                  <Chip
                    label={`${foodcourt.restaurants?.length || 0} Restaurants`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push(`/foodcourt/${foodcourt.id}`);
                    }}
                  >
                    Edit
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(e, foodcourt.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default FoodcourtList;
